/**
 * authFailure.js
 *
 * Centralized handler for OAuth/API auth-failure responses.
 *
 * Detected conditions:
 *   Case 1 — OAuth invalid_scope:
 *     { "error": "invalid_scope", ... }
 *
 *   Case 2 — Resource owner / auth server denied:
 *     { "message": "The resource owner or authorization server denied the request." }
 *
 * When either is detected for a signed-in user:
 *   1. Calls persistor.purge() — clears localStorage and dispatches PURGE to
 *      reset Redux auth state to INITIAL_STATE (signedIn: false).
 *   2. ProtectedRoute in App.jsx reacts to signedIn = false and automatically
 *      redirects to /user/login. No explicit navigate() call is needed here.
 *
 * Safety guarantees:
 *   - Only fires when the user is currently signed in (prevents no-op loops).
 *   - A module-level flag (_logoutInFlight) ensures concurrent failing requests
 *     only trigger one logout sequence, eliminating race conditions.
 *   - The flag is reset after the purge settles so the handler stays usable
 *     if the app remains mounted (e.g., HMR in development).
 */

let _persistor = null;
let _store = null;
let _logoutInFlight = false;

/**
 * Called once from src/redux/index.js after the store and persistor are
 * created. Mirrors the injectStore* pattern used by api.js, general.js, etc.
 *
 * @param {{ persistor: object, store: object }} deps
 */
export const injectDepsAuthFailure = ({ persistor, store }) => {
  _persistor = persistor;
  _store = store;
};

/**
 * Returns true when an API response payload represents an auth failure that
 * requires the current session to be cleared.
 *
 * Intentionally strict equality checks — avoids false positives from
 * unrelated API errors that happen to share similar message patterns.
 *
 * @param {unknown} payload  Parsed JSON response body
 * @returns {boolean}
 */
export const isAuthFailureResponse = (payload) => {
  if (!payload || typeof payload !== 'object') return false;

  // Case 1: OAuth token endpoint returns invalid_scope
  if (payload.error === 'invalid_scope') return true;

  // Case 2: Any API returns the OAuth-denied message
  if (
    typeof payload.message === 'string' &&
    payload.message === 'The resource owner or authorization server denied the request.'
  ) return true;

  return false;
};

/**
 * Idempotent auth-failure handler. Safe to call from multiple simultaneous
 * in-flight requests — only the first call per session triggers a logout.
 *
 * Does NOT call navigate() — the ProtectedRoute component handles redirect
 * reactively once signedIn becomes false in Redux.
 */
export const handleAuthFailure = () => {
  // Guard: only act if the user is currently signed in.
  const signedIn = _store?.getState?.()?.auth?.signedIn;
  if (!signedIn) return;

  // Guard: prevent concurrent duplicate calls.
  if (_logoutInFlight) return;
  _logoutInFlight = true;

  console.warn(
    '[authFailure] Auth failure detected (invalid_scope or denied request) — clearing session.',
  );

  // purge() clears the redux-persist localStorage entry and dispatches the
  // PURGE action, which resets the auth reducer to INITIAL_STATE.
  // The returned promise always resolves; we reset the flag in finally so
  // the handler works correctly if the module stays alive (e.g., dev HMR).
  _persistor
    ?.purge?.()
    .catch((err) => {
      console.error('[authFailure] persistor.purge() failed:', err);
    })
    .finally(() => {
      _logoutInFlight = false;
    });
};
